import { supabase } from "@/lib/supabase/client";
import type { Database, TableRow } from "@/types/database";

export type Shipment = TableRow<"shipments">;
export type Container = TableRow<"containers">;
export type ShipmentMilestone = TableRow<"shipment_milestones">;
export type ShipmentInput = Omit<Database["public"]["Tables"]["shipments"]["Insert"], "tenant_id" | "created_by">;
export type ContainerInput = Omit<Database["public"]["Tables"]["containers"]["Insert"], "tenant_id">;
export type MilestoneInput = Omit<Database["public"]["Tables"]["shipment_milestones"]["Insert"], "tenant_id" | "created_by">;

const shipmentColumns = "id, tenant_id, customer_id, job_number, shipment_type, transport_mode, status, origin, destination, carrier_name, booking_number, bill_of_lading, etd, eta, atd, ata, notes, created_at, updated_at, created_by";
const containerColumns = "id, tenant_id, shipment_id, container_number, container_type, seal_number, status, created_at, updated_at";
const milestoneColumns = "id, tenant_id, shipment_id, container_id, event_type, event_name, location, planned_at, actual_at, source, notes, created_at, updated_at, created_by, shipment_tracking_id, tracking_event_fingerprint";

export async function listShipments(tenantId: string) {
  const { data, error } = await supabase
    .from("shipments")
    .select(shipmentColumns)
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getShipment(tenantId: string, shipmentId: string) {
  const { data, error } = await supabase
    .from("shipments")
    .select(shipmentColumns)
    .eq("tenant_id", tenantId)
    .eq("id", shipmentId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createShipment(tenantId: string, input: ShipmentInput) {
  const { data, error } = await supabase
    .from("shipments")
    .insert({ ...input, tenant_id: tenantId })
    .select(shipmentColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function updateShipment(tenantId: string, shipmentId: string, input: Partial<ShipmentInput>) {
  const { data, error } = await supabase
    .from("shipments")
    .update(input)
    .eq("tenant_id", tenantId)
    .eq("id", shipmentId)
    .select(shipmentColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function listContainers(tenantId: string, shipmentId: string) {
  const { data, error } = await supabase
    .from("containers")
    .select(containerColumns)
    .eq("tenant_id", tenantId)
    .eq("shipment_id", shipmentId)
    .order("container_number");
  if (error) throw error;
  return data ?? [];
}

export async function createContainer(tenantId: string, shipmentId: string, input: Omit<ContainerInput, "shipment_id">) {
  const { data, error } = await supabase
    .from("containers")
    .insert({ ...input, tenant_id: tenantId, shipment_id: shipmentId })
    .select(containerColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function updateContainer(tenantId: string, containerId: string, input: Partial<ContainerInput>) {
  const { data, error } = await supabase
    .from("containers")
    .update(input)
    .eq("tenant_id", tenantId)
    .eq("id", containerId)
    .select(containerColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteContainer(tenantId: string, containerId: string) {
  const { error } = await supabase.from("containers").delete().eq("tenant_id", tenantId).eq("id", containerId);
  if (error) throw error;
}

export async function listMilestones(tenantId: string, shipmentId: string) {
  const { data, error } = await supabase
    .from("shipment_milestones")
    .select(milestoneColumns)
    .eq("tenant_id", tenantId)
    .eq("shipment_id", shipmentId)
    .order("actual_at", { ascending: true, nullsFirst: false })
    .order("planned_at", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return data ?? [];
}

export async function createMilestone(tenantId: string, shipmentId: string, input: Omit<MilestoneInput, "shipment_id">) {
  const { data, error } = await supabase
    .from("shipment_milestones")
    .insert({ ...input, tenant_id: tenantId, shipment_id: shipmentId, source: "MANUAL" })
    .select(milestoneColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function updateMilestone(tenantId: string, milestoneId: string, input: Partial<MilestoneInput>) {
  const { data, error } = await supabase
    .from("shipment_milestones")
    .update(input)
    .eq("tenant_id", tenantId)
    .eq("id", milestoneId)
    .select(milestoneColumns)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMilestone(tenantId: string, milestoneId: string) {
  const { error } = await supabase.from("shipment_milestones").delete().eq("tenant_id", tenantId).eq("id", milestoneId);
  if (error) throw error;
}
